import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { FileService } from '@core/services/file.service';
import {
  FileConfig,
  FileUpload,
  IFileConfig,
  IValidationMessage,
} from '@models/file';
import { UploadValidation } from '@utils/validation/upload-validation';

@Component({
  selector: 'app-document-uploader',
  template: `
    <div class="col justify-center align-items-centered gap-2">
      <ng-container *ngIf="currentDocument && fileName">
        <a [href]="currentDocument" target="_blank"
          ><mat-icon>description</mat-icon> {{ fileName }}</a
        >
        <ng-container *ngIf="!thumbnail">
          Generating Document Thumbnail
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        </ng-container>
      </ng-container>

      <app-base-uploader
        [currentImageFile]="thumbnail"
        [fileConfig]="fileConfig"
        [fileListFormControl]="fileListFormControl"
        [form]="form"
        [validationMessageList]="validationMessageList"
        [disabled]="disabled"
        (emitFileSelect)="onFileSelect($event)"
        (fileUploadsEmitter)="fileUploadsEmitter.emit($event)"
        [required]="required"
      ></app-base-uploader>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentUploaderComponent implements OnInit {
  @Input() public currentDocument: string;
  @Input() public fileName: string;
  @Input() public thumbnail: string;
  @Input() public fileListFormControlName: string = 'formValue';
  /**
   * The uploader requires the parent component to set up the form that
   * will contain the `FormControl` for the `FileList`.
   */
  @Input() public form: UntypedFormGroup;
  @Output() fileUploadsEmitter: EventEmitter<FileUpload[]> = new EventEmitter();
  @Input() public disabled: boolean = false;
  @Input() public required: boolean = false;
  @Output() public emitFileValid = new EventEmitter<boolean>();

  public fileConfig = new FileConfig({
    allowedFileExtensionList: ['pdf', 'png', 'jpg', 'jpeg', 'gif'],
    allowedMimeTypeList: ['application/pdf', 'image/*'],
    canUploadMultiple: false,
  });
  public validationMessageList: IValidationMessage[] = [];

  constructor(
    private fileService: FileService,
    private fb: UntypedFormBuilder,
  ) { }

  public ngOnInit(): void {
    if (!this.form) {
      this.form = this.fb.group({
        formValue: new UntypedFormControl('', Validators.required),
      });
    }
    this.setFileListFormControlValidation(this.fileConfig);
  }

  public get fileListFormControl(): AbstractControl {
    return this.form.get(this.fileListFormControlName);
  }

  public onFileSelect(fileList: FileList): void {
    if (fileList?.length > 0) {
      this.validationMessageList = this.validateFiles(fileList);
      this.fileListFormControl.setValue(fileList);
      this.validationMessageList.length
        ? this.emitFileValid.emit(true)
        : this.emitFileValid.emit(false);
    }
  }

  private setFileListFormControlValidation(fileConfig: IFileConfig): void {
    this.fileListFormControl?.setValidators([
      UploadValidation.validateFiles(fileConfig),
    ]);
    this.fileListFormControl?.updateValueAndValidity();
  }

  private validateFiles(fileList: FileList): IValidationMessage[] {
    return this.fileService.buildValidationSuccessMessageList(
      fileList,
      this.fileConfig,
    );
  }
}
